<template>
  <div class="checkout-products">
    <div class="py-8">
      <h2 class="text-xl sm:text-2xl font-extrabold text-custom-text">
        {{ t("title") }}
      </h2>
      <p class="sm:text-lg font-medium mt-4 text-custom-grey-1">
        {{ t("subtitle") }}
      </p>
    </div>

    <div v-if="products.length === 0" class="empty-state py-8 text-center">
      <p class="text-custom-grey-1">{{ t("no_products") }}</p>
    </div>

    <div v-else class="product-grid space-y-4">
      <div
        v-for="product in products"
        :key="product.id"
        class="product-card border border-custom-grey-7 rounded-lg p-4 hover:border-primary transition-colors duration-200 relative"
        :class="{ 'border-primary bg-primary-50': isInOrder(product) }"
      >
        <!-- Checkmark indicator -->
        <div
          v-if="isInOrder(product)"
          class="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center"
        >
          <svg
            class="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div class="flex items-start gap-4">
          <button
            v-if="product.featured_image_url"
            type="button"
            @click="openModal(product)"
            class="flex-none w-20 h-20 rounded-md overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :aria-label="t('view_details_aria', { name: product.title })"
          >
            <img
              :src="product.featured_image_url"
              :alt="product.title"
              class="w-full h-full object-cover"
            />
          </button>
          <button
            v-else
            type="button"
            @click="openModal(product)"
            class="flex-none w-20 h-20 rounded-md bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :aria-label="t('view_details_aria', { name: product.title })"
          >
            <svg
              class="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </button>
          <div class="flex-1 min-w-0">
            <button
              type="button"
              @click="openModal(product)"
              class="font-bold text-custom-text truncate block w-full text-left hover:text-primary transition-colors focus:outline-none focus:underline"
            >
              {{ product.title }}
            </button>
            <p
              v-if="product.brief_description"
              class="text-sm text-custom-grey-1 mt-1 line-clamp-2"
            >
              {{ product.brief_description }}
            </p>
            <button
              v-if="product.description"
              type="button"
              @click="openModal(product)"
              class="text-xs text-primary hover:text-primary-700 mt-1 focus:outline-none focus:underline"
            >
              {{ t("view_details") }}
            </button>
            <div class="mt-2 flex items-center justify-between">
              <div>
                <span class="font-bold text-custom-text">
                  {{
                    formatPriceCents(
                      product.price_in_cents,
                      product.price_currency
                    )
                  }}
                </span>
                <span class="text-xs text-custom-grey-1 ml-1">
                  {{ t("vat") }}
                </span>
              </div>
              <button
                type="button"
                @click="toggleProduct(product)"
                class="px-4 py-2 text-sm font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                :class="
                  isInOrder(product)
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-primary text-white hover:bg-primary-700'
                "
                :disabled="saving"
                :aria-label="
                  isInOrder(product)
                    ? t('remove_aria', { name: product.title })
                    : t('add_aria', { name: product.title })
                "
              >
                <span v-if="saving && savingProductId === product.id">
                  {{ t("saving") }}
                </span>
                <span v-else-if="isInOrder(product)">
                  {{ t("remove") }}
                </span>
                <span v-else>
                  {{ t("add_to_order") }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Details Modal -->
    <ProductModal
      :product="selectedProduct"
      :is-in-order="selectedProduct ? isInOrder(selectedProduct) : false"
      :saving="saving && savingProductId === selectedProduct?.id"
      @close="closeModal"
      @toggle="toggleProductFromModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { IProduct, IOrderLine } from "@/scripts/application/types";
import { useI18n } from "vue-i18n";
import { useCurrency } from "@/scripts/application/composables/useCurrency";
import ProductModal from "./ProductModal.vue";

const props = defineProps<{
  products: IProduct[];
  orderLines: IOrderLine[];
  saving?: boolean;
}>();

const emit = defineEmits<{
  (event: "add-product", product: IProduct): void;
  (event: "remove-product", product: IProduct): void;
}>();

const { t } = useI18n({ inheritLocale: true, useScope: "local" });
const { formatPriceCents } = useCurrency();

const savingProductId = ref<number | null>(null);
const selectedProduct = ref<IProduct | null>(null);

const orderLineProductIds = computed(() =>
  props.orderLines
    .filter((line) => !line._destroy)
    .map((line) => line.orderable_id)
);

function isInOrder(product: IProduct): boolean {
  return orderLineProductIds.value.includes(product.id);
}

function toggleProduct(product: IProduct) {
  savingProductId.value = product.id;
  if (isInOrder(product)) {
    emit("remove-product", product);
  } else {
    emit("add-product", product);
  }
}

function openModal(product: IProduct) {
  selectedProduct.value = product;
}

function closeModal() {
  selectedProduct.value = null;
}

function toggleProductFromModal(product: IProduct) {
  toggleProduct(product);
}
</script>

<i18n lang="yaml">
en:
  title: "Upgrade your learning experience"
  subtitle: "Enhance your learning outcome with these addons:"
  no_products: "No additional products available for this course"
  add_to_order: "Add to order"
  remove: "Remove"
  back: "Back"
  continue: "Continue to payment"
  saving: "Saving..."
  view_details: "View details"
  view_details_aria: "View details for {name}"
  add_aria: "Add {name} to your order"
  remove_aria: "Remove {name} from your order"
  vat: "incl. VAT"
nb:
  title: "Oppgrader kursopplevelsen din"
  subtitle: "Få bedre læringsutbytte med disse tilleggene:"
  no_products: "Ingen ekstra produkter tilgjengelig for dette kurset"
  add_to_order: "Legg til"
  remove: "Fjern"
  back: "Tilbake"
  continue: "Fortsett til betaling"
  saving: "Lagrer..."
  view_details: "Se detaljer"
  view_details_aria: "Se detaljer for {name}"
  add_aria: "Legg {name} til bestillingen din"
  remove_aria: "Fjern {name} fra bestillingen din"
  vat: "inkl. MVA"
uk:
  title: "Покращте свій досвід навчання"
  subtitle: "Покращте своє навчання з цими додатками"
  no_products: "Немає додаткових продуктів для цього курсу"
  add_to_order: "Додати"
  remove: "Видалити"
  back: "Назад"
  continue: "Перейти до оплати"
  saving: "Збереження..."
  view_details: "Переглянути деталі"
  view_details_aria: "Переглянути деталі для {name}"
  add_aria: "Додати {name} до замовлення"
  remove_aria: "Видалити {name} з замовлення"
  vat: "з ПДВ"
es:
  title: "Mejora tu experiencia de aprendizaje"
  subtitle: "Potencia tu aprendizaje con estos complementos:"
  no_products: "No hay productos adicionales disponibles para este curso"
  add_to_order: "Añadir"
  remove: "Eliminar"
  back: "Volver"
  continue: "Continuar al pago"
  saving: "Guardando..."
  view_details: "Ver detalles"
  view_details_aria: "Ver detalles de {name}"
  add_aria: "Añadir {name} a tu pedido"
  remove_aria: "Eliminar {name} de tu pedido"
  vat: "IVA incl."
pl:
  title: "Ulepsz swoje doświadczenie edukacyjne"
  subtitle: "Wzbogać swoją naukę o te dodatki:"
  no_products: "Brak dodatkowych produktów dla tego kursu"
  add_to_order: "Dodaj"
  remove: "Usuń"
  back: "Wstecz"
  continue: "Przejdź do płatności"
  saving: "Zapisywanie..."
  view_details: "Zobacz szczegóły"
  view_details_aria: "Zobacz szczegóły dla {name}"
  add_aria: "Dodaj {name} do zamówienia"
  remove_aria: "Usuń {name} z zamówienia"
  vat: "z VAT"
</i18n>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
